/**
 * Close markdown tokens that a stream has opened but not yet finished.
 *
 * While a turn streams, `Response` is handed a prefix of the final answer, so
 * it regularly receives text mid-token. Plain `react-markdown` renders those
 * prefixes literally: `The wedge is **texture` shows the asterisks, `[the
 * report](https://exa` shows raw bracket-and-paren text, and an open ``` fence
 * renders the rest of the answer as prose. It self-corrects the moment the
 * token closes, so this is visible flicker on nearly every streamed answer
 * rather than a correctness bug — but it is the single most-seen surface in
 * the product, and it flickers on every bolded phrase.
 *
 * WHY THIS AND NOT `streamdown`. Vercel's AI Elements `Response` upstream is a
 * thin wrapper around `streamdown`, whose core does exactly this. Adopting it
 * was considered and rejected for now on evidence, not preference:
 *   - `streamdown` is not referenced anywhere in the installed `ai@7.0.3` docs
 *     (`node_modules/ai/docs/`), and `ai-elements` is not an installed package
 *     — these components are vendored into `src/components/ai-elements/`. So
 *     there is no upstream contract we are violating by not using it.
 *   - Its plugin set pulls in `marked`, `shiki` and `mermaid`, which is a large
 *     bundle cost on the surface the app loads most.
 *   - Tailwind v4 would need `@source "../node_modules/streamdown/dist/*.js"`
 *     or its classes get pruned — a trap this repo has already been bitten by.
 *   - `response.tsx`'s internal-link → React Router `<Link>` renderer is
 *     Mora-specific and was added after a real founder-reported incident; a
 *     migration has to re-verify it.
 * Less, but better: fix the defect, keep the dependency budget. Revisit if we
 * ever need Shiki highlighting or Mermaid, which would change the trade.
 *
 * DELIBERATELY NOT HANDLED: `_italic_`. Underscores appear constantly inside
 * identifiers this product actually prints (`primary_texts`, `ad_units`,
 * `resolve_account_owner`), and markdown does not treat intra-word underscores
 * as emphasis. Auto-closing them would corrupt real content to fix a rarer
 * flicker than `**` — a bad trade.
 */

/** Sentinel for masked inline-code spans. Not producible by a markdown stream. */
const MASK_OPEN = '\u0000CODE';
const MASK_CLOSE = '\u0000';

function countOccurrences(text: string, pattern: RegExp): number {
  return (text.match(pattern) ?? []).length;
}

export function completePartialMarkdown(input: string): string {
  if (!input) return input;

  // A fenced code block swallows everything after it, so an odd number of
  // fences means we are INSIDE code. Close the fence and stop — balancing
  // inline emphasis against code contents would corrupt the code.
  if (countOccurrences(input, /^[ \t]*```/gm) % 2 === 1) {
    return `${input}\n\`\`\``;
  }

  const masked: string[] = [];
  const mask = (value: string) => {
    masked.push(value);
    return `${MASK_OPEN}${masked.length - 1}${MASK_CLOSE}`;
  };

  // Complete fenced blocks are settled content. They have to come out before
  // anything counts backticks, or the three closing backticks of a finished
  // block read as an unterminated inline span.
  let out = input.replace(/```[\s\S]*?```/g, mask);

  // An unterminated link or image at the tail. Until the closing paren
  // arrives, show the label as plain text (and nothing at all for an image,
  // whose alt text is not the content the reader is waiting for).
  out = out.replace(/(!?)\[([^\]]*)\]\([^)\s]*$/, (_match, bang: string, label: string) =>
    bang ? '' : label,
  );
  out = out.replace(/(!?)\[([^\]\n]*)$/, (_match, bang: string, label: string) =>
    bang ? '' : label,
  );

  // Mask complete inline-code spans so their contents can't be mistaken for
  // emphasis markers, then close a span the stream left open.
  out = out.replace(/`[^`\n]*`/g, mask);
  if (out.includes('`')) {
    out = `${out}\``;
  }

  // Three constructs spend asterisks and only one of them is emphasis, so
  // strip the other two ONCE, before any counting: a `* ` bullet marker (an
  // odd-length list would otherwise gain a stray trailing asterisk) and a
  // `***` thematic break (which contains a `**` and so trips the bold count).
  const scannable = out
    .replace(/^[ \t]*(?:\*{3,}|_{3,}|-{3,})[ \t]*$/gm, '')
    .replace(/^[ \t]*\*[ \t]+/gm, '');

  if (countOccurrences(scannable, /~~/g) % 2 === 1) {
    out = `${out}~~`;
  }

  if (countOccurrences(scannable, /\*\*/g) % 2 === 1) {
    out = `${out}**`;
  }

  if (countOccurrences(scannable.replace(/\*\*/g, ''), /\*/g) % 2 === 1) {
    out = `${out}*`;
  }

  return out.replace(
    new RegExp(`${MASK_OPEN}(\\d+)${MASK_CLOSE}`, 'g'),
    (_match, index: string) => masked[Number(index)],
  );
}
